import React, { FC, useCallback, useState } from 'react';
import { LayoutChangeEvent, ScrollView, View } from 'react-native';
import { scale } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { WithColorProps } from '@styles/colors/types';
import { useMachinesNowOverviewViewModel } from '@v2/pages/machines/modules/now/components/Overview/hooks/useMachinesNowOverviewViewModel';
import { MachineActivityBlock } from './MachineActivityBlock/MachineActivityBlock';
import { OeeTodayBlock } from './OeeTodayBlock/OeeTodayBlock';
import ToolsBlock from './ToolsBlock';
import NcFileBlock from './NCFileBlock';
import { SCREEN_PADDING_HORIZONTAL, SCREEN_PADDING_HORIZONTAL_FOR_ITEM, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { MACHINES_NOW_OVERVIEW_BLOCK_CONFIG, OEE_TOOLS_BLOCK_CONFIG, NC_FILE_LARGE_MODE_THRESHOLD } from '@v2/pages/machines/modules/now/components/Overview/constants';
import { isPhone, isWeb } from '@helpers';
import { role } from '@components/frame';
import { isRestrictedForOperator, RESTRICTION_CATEGORIES } from '@config/operatorRestrictions';
import TopBlock from './TopBlock';
import BatchBlock from './BatchBlock';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { MachineStatusGauge } from '@v2/pages/machines/common/components/MachineStatusGauge/MachineStatusGauge';
import { InlineErrorBoundary } from '@v2/components/errorBoundary';

interface MachinesNowOverviewProps extends Pick<WithColorProps, 'C'> {}

const MachinesNowOverviewComponent: FC<MachinesNowOverviewProps> = ({ C }) => {
  const { oeeTeepData, toolsLength, ncFileFirstRanInfo } = useMachinesNowOverviewViewModel();
  const { isAllSelected } = useHandleParams();

  const [containerHeight, setContainerHeight] = useState(0);
  const [aboveNcFileHeight, setAboveNcFileHeight] = useState(0);

  const handleContainerLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerHeight(event.nativeEvent.layout.height);
  }, []);

  const handleAboveBlocksLayout = useCallback((event: LayoutChangeEvent) => {
    setAboveNcFileHeight(event.nativeEvent.layout.height);
  }, []);

  const { maxWidth, gapBetweenBlocks } = MACHINES_NOW_OVERVIEW_BLOCK_CONFIG;
  const { height: oeeToolsBlockHeight } = OEE_TOOLS_BLOCK_CONFIG;

  const available = containerHeight - aboveNcFileHeight - scale(SCREEN_PADDING_HORIZONTAL_FOR_ITEM) - scale(gapBetweenBlocks);
  const ncFileLargeMode = isWeb && !isPhone && containerHeight > 0 && available >= scale(NC_FILE_LARGE_MODE_THRESHOLD);

  if (isAllSelected) {
    return (
      <ScrollView
        testID={TEST_IDS.liveScreen.allMachinesBlock}
        style={{ flex: 1 }}
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: 'center',
          paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
          alignItems: 'center',
        }}
        showsVerticalScrollIndicator={false}
      >
        <MachineStatusGauge />
      </ScrollView>
    );
  }

  return (
    <View
      testID={TEST_IDS.liveScreen.singleMachineOverview}
      style={{
        flex: 1,
        paddingTop: scale(SCREEN_PADDING_HORIZONTAL_FOR_ITEM),
        paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
        alignItems: 'center',
      }}
      onLayout={handleContainerLayout}
    >
      <View style={{ flex: 1, maxWidth: scale(maxWidth), width: '100%', gap: scale(gapBetweenBlocks) }}>
        <View onLayout={handleAboveBlocksLayout} style={{ gap: scale(gapBetweenBlocks) }}>
          <InlineErrorBoundary screenName="TopBlock">
            <TopBlock ncFileFirstRanInfo={ncFileFirstRanInfo} />
          </InlineErrorBoundary>
          <InlineErrorBoundary screenName="BatchBlock">
            <BatchBlock />
          </InlineErrorBoundary>
          <InlineErrorBoundary screenName="MachineActivityBlock">
            <MachineActivityBlock C={C} />
          </InlineErrorBoundary>
          <View style={{ flexDirection: isPhone ? 'column' : 'row', gap: scale(gapBetweenBlocks) }}>
            {!isRestrictedForOperator(role, RESTRICTION_CATEGORIES.LIVE_OVERVIEW_HIDDEN, 'OeeTodayBlock') && (
              <View style={{ flex: 1, flexBasis: scale(oeeToolsBlockHeight) }}>
                <InlineErrorBoundary screenName="OeeTodayBlock" compact>
                  <OeeTodayBlock C={C} oeeTeepData={oeeTeepData} />
                </InlineErrorBoundary>
              </View>
            )}
            <View style={{ flex: 1, flexBasis: scale(oeeToolsBlockHeight) }}>
              <InlineErrorBoundary screenName="ToolsBlock" compact>
                <ToolsBlock C={C} toolsLength={toolsLength} />
              </InlineErrorBoundary>
            </View>
          </View>
        </View>
        <InlineErrorBoundary screenName="NcFileBlock">
          <NcFileBlock C={C} largeMode={ncFileLargeMode} largeHeight={ncFileLargeMode ? available : undefined} />
        </InlineErrorBoundary>
      </View>
    </View>
  );
};

const MachinesNowOverview = ColorWrapper(MachinesNowOverviewComponent);

export { MachinesNowOverview };
